import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { bezuege, hatBlattuebergreifendenBezug } from "@/office/excel/formeln";
import { formulaCells, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionBlattuebergreifend: Lesson = {
  id: "blattuebergreifend",
  title: "Über Blattgrenzen hinweg rechnen",
  summary: "Rohdaten hier, Auswertung dort – und eine Formel, die beides verbindet.",
  durationMinutes: 9,
  learningGoals: [
    "Formeln über Blattgrenzen hinweg aufbauen",
    "Ein Blatt für Einstellungen anlegen und darauf verweisen",
    "Die Grenzen von Verweisen auf andere Dateien einschätzen",
  ],
  steps: [
    {
      id: "blattbezug",
      title: "Der Blattname mit Ausrufezeichen",
      explanation: [
        "Ein Bezug auf ein anderes Blatt sieht aus wie ein normaler Bezug mit einem " +
          "Vorspann: =Einstellungen!B1 holt den Inhalt von B1 aus dem Blatt " +
          "„Einstellungen“. Getippt wird auch das nicht – während die Formel offen ist, " +
          "wechselst du einfach aufs andere Blatt und klickst die Zelle an. Excel setzt " +
          "den Blattnamen selbst davor und kehrt beim Bestätigen zurück.",
        "Damit wird die Arbeitsteilung von Modul 1 praktisch nutzbar: Rohdaten auf dem " +
          "einen Blatt, Auswertung auf dem nächsten, alle veränderlichen Werte auf " +
          "einem Blatt „Einstellungen“. Die Auswertung greift über Blattgrenzen darauf " +
          "zu, ohne dass die Rohdaten angefasst werden müssen.",
        "Wird das Blatt später umbenannt, zieht Excel alle Formelverweise automatisch " +
          "mit. Wird es gelöscht, hinterlässt es #BEZUG! – und zwar in jeder Formel, " +
          "die darauf zeigte. Vor dem Löschen eines Blattes lohnt deshalb immer der " +
          "Blick, ob etwas darauf verweist.",
        "Auch über Dateigrenzen hinweg geht das, sieht dann aber so aus: " +
          "='C:\\Ordner\\[Mappe.xlsx]Tabelle1'!$B$1. Möglich, aber heikel: Der Verweis " +
          "bricht, sobald jemand die andere Datei verschiebt oder umbenennt, und beim " +
          "Öffnen fragt Excel jedes Mal nach dem Aktualisieren. Wo es sich vermeiden " +
          "lässt, gehören zusammengehörige Daten in eine Datei.",
      ],
      keyPoints: [
        "Blattname, Ausrufezeichen, Zelladresse: =Einstellungen!B1.",
        "Während die Formel offen ist, auf das andere Blatt klicken – Excel schreibt mit.",
        "Verweise auf andere Dateien brechen, sobald die Datei umzieht.",
      ],
      task: {
        instruction:
          "Lege auf einem zweiten Blatt eine Auswertung oder einen Parameter an und " +
          "baue eine Formel, die über die Blattgrenze hinweg darauf verweist.",
        hint: "Formel beginnen, dann unten auf das andere Blattregister klicken und dort die Zelle anklicken.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob vor der Zelladresse ein Blattname mit Ausrufezeichen steht.",
              true,
            );
          }

          if (mappe.sheets.length < 2) {
            return fail(
              "Die Arbeitsmappe hat nur ein Tabellenblatt.",
              "Lege mit Umschalt + F11 ein zweites Blatt an – dann gibt es überhaupt eine Blattgrenze.",
            );
          }

          const formeln = formulaCells(mappe);
          const uebergreifend = formeln.filter((zelle) => hatBlattuebergreifendenBezug(zelle.formula));

          if (uebergreifend.length === 0) {
            return fail(
              `Keine der ${formeln.length} Formeln verweist auf ein anderes Blatt.`,
              "Schreibe auf einem Blatt ein Gleichheitszeichen, wechsle unten auf ein anderes Blatt und " +
                "klicke dort eine Zelle an.",
            );
          }

          const erste = uebergreifend[0];
          const ziele = new Set(
            bezuege(erste.formula)
              .map((bezug) => bezug.sheet)
              .filter((name): name is string => name !== null),
          );
          return pass(
            `In ${erste.address} greift „${erste.formula}“ auf ` +
              `${[...ziele].map((name) => `„${name}“`).join(" und ")} zu – die Auswertung liegt getrennt von den Daten.`,
          );
        },
      },
    },
    {
      id: "formeln-anzeigen",
      title: "Eine fremde Tabelle prüfen",
      explanation: [
        "Eine Tabelle zeigt Ergebnisse. Was dahintersteckt, sieht man erst, wenn man " +
          "gezielt hinschaut – und dafür gibt es drei Werkzeuge, die zusammen fast " +
          "jeden Fehler finden.",
        "Strg + # schaltet die Formelanzeige um: Statt der Ergebnisse zeigt jede Zelle " +
          "ihre Formel. Auf einen Blick sieht man dann, welche Zellen gerechnet und " +
          "welche getippt sind – und wo in einer Spalte plötzlich eine Formel fehlt " +
          "oder abweicht. Dieselbe Tastenkombination schaltet wieder zurück.",
        "Unter „Formeln“ → „Formelüberwachung“ liegen „Spur zum Vorgänger“ und " +
          "„Spur zum Nachfolger“. Sie zeichnen Pfeile von einer Zelle zu allen Zellen, " +
          "aus denen sie ihre Werte holt beziehungsweise die von ihr abhängen. Bei einer " +
          "unübersichtlichen Rechnung ist das der schnellste Weg zum Verständnis.",
        "Und die Formelauswertung – ebenfalls in dieser Gruppe – rechnet eine " +
          "verschachtelte Formel Schritt für Schritt vor. Bei einer WENN-Funktion in " +
          "einer WENN-Funktion in einem SVERWEIS ist das oft der einzige Weg, den " +
          "Fehler zu finden.",
      ],
      keyPoints: [
        "Strg + # zeigt alle Formeln statt der Ergebnisse an.",
        "Formeln → Formelüberwachung → Spur zum Vorgänger zeichnet die Herkunft ein.",
        "Die Formelauswertung rechnet eine verschachtelte Formel Schritt für Schritt vor.",
      ],
      task: {
        instruction:
          "Schalte mit Strg + # die Formelanzeige ein und prüfe deine Blätter: Steht in " +
          "jeder Spalte, die gerechnet werden soll, wirklich überall eine Formel – oder " +
          "hat sich irgendwo ein getippter Wert dazwischengeschmuggelt?",
        hint: "Getippte Werte in einer Formelspalte erkennst du in der Formelanzeige sofort: Dort steht eine Zahl statt eines Gleichheitszeichens.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Schalte mit Strg + # die Formelanzeige ein und sieh die Rechenspalten durch.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          if (formeln.length === 0) {
            return fail(
              "Es steht noch keine Formel in der Arbeitsmappe.",
              "Löse zuerst die Aufgaben der vorigen Lektionen.",
            );
          }

          // Eine Rechenspalte ist eine Spalte, in der mehrheitlich Formeln
          // stehen. Ein getippter Wert dazwischen ist der gesuchte Fehler.
          const proSpalte = new Map<string, { formeln: number; werte: string[] }>();
          for (const blatt of mappe.sheets) {
            for (const zelle of blatt.cells) {
              if (zelle.valueType === "Empty") continue;
              const schluessel = `${blatt.name}|${zelle.column}`;
              const eintrag = proSpalte.get(schluessel) ?? { formeln: 0, werte: [] };
              if (zelle.formula.startsWith("=")) eintrag.formeln += 1;
              else eintrag.werte.push(zelle.address);
              proSpalte.set(schluessel, eintrag);
            }
          }

          const luecken = [...proSpalte.entries()]
            .map(([schluessel, eintrag]) => ({ blatt: schluessel.split("|")[0], ...eintrag }))
            // Mehrheitlich Formeln, aber nicht ausschließlich. Die Kopfzeile
            // zählt als ein erlaubter Textwert.
            .filter((eintrag) => eintrag.formeln >= 3 && eintrag.werte.length >= 2);

          if (luecken.length === 0) {
            return pass(
              `Jede Rechenspalte enthält durchgehend Formeln – von ${formeln.length} Formeln ` +
                "unterbricht keine einzelne Zahl die Reihe.",
            );
          }

          const erste = luecken[0];
          return fail(
            `Auf „${erste.blatt}“ steht eine Spalte mit ${erste.formeln} Formeln, in der ` +
              `${erste.werte.length} Zellen einen festen Wert statt einer Formel tragen: ` +
              `${erste.werte.slice(0, 3).join(", ")}.`,
            "Wenn das Absicht ist – etwa eine Beschriftung –, ist alles gut. Ist es keine, fülle die " +
              "Formel von oben über diese Zellen hinweg aus.",
          );
        },
      },
    },
  ],
};
